import { SearchOutlined } from '@ant-design/icons';
import { Input, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { listSkills } from '../../services/skillsApi';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { formatSkillDisplayName, groupSkillsByCategory } from './skillsConfig';
import SkillCard from './SkillCard';
import SkillsApiKeyCTA from './SkillsApiKeyCTA';
import './SkillsPage.css';

const SkillsPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        setLoading(true);
        const response = await listSkills();
        setSkills(response.data.skills || []);
      } catch (err) {
        console.error('Failed to fetch skills:', err);
        setError('Failed to load skills');
      } finally {
        setLoading(false);
      }
    };
    fetchSkills();
  }, []);

  const handleSkillClick = (slug) => {
    navigate(`/skills/${slug}`);
  };

  // Filter skills based on search
  const filteredSkills = searchValue
    ? skills.filter(skill =>
        skill.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        formatSkillDisplayName(skill.name).toLowerCase().includes(searchValue.toLowerCase()) ||
        skill.description.toLowerCase().includes(searchValue.toLowerCase()) ||
        skill.slug.toLowerCase().includes(searchValue.toLowerCase())
      )
    : skills;

  const groupedSkills = groupSkillsByCategory(filteredSkills);

  // Renamed from "OpenClaw Skills" — the skills catalog is agent-agnostic:
  // works across OpenClaw, Cursor, Claude Code, OpenAI, Perplexity, and any
  // HTTP client. The title should reflect that broader scope.
  const seoTitle = 'Agent-to-agent skills — CellCog AI Agent Capabilities';
  const seoDescription = `Explore ${skills.length} AI skills your agents can delegate to CellCog. Research, video, image, audio, documents, dashboards, and more — works across every major agent framework.`;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={`${SEO_DEFAULTS.url}/skills`} />
        {/* Out of SEO scope (Nitish, Sep 3 2026; CEL-2148): noindex for search engines, but agents
            get the catalog as markdown (public/skills/*, emitted by scripts/copySkillsDocs.js). */}
        <meta name="robots" content="noindex, follow" />
        <link rel="alternate" type="text/markdown" href={`${SEO_DEFAULTS.url}/skills/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={`${SEO_DEFAULTS.url}/skills`} />
        <meta property="og:image" content={SEO_DEFAULTS.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
      </Helmet>

      <div className={`skills-page ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="skills-page-scroll">
          {/* Hero */}
          <div className="skills-hero">
            <h1 className="skills-hero-title">Agent-to-agent skills</h1>
            <p className="skills-hero-subtitle">
              {skills.length} capabilities your agents can delegate to CellCog — across every major framework
            </p>
            <div className="skills-hero-agents">
              <span className="skills-hero-agents-label">Works with</span>
              <div className="skills-hero-agents-logos">
                <img
                  src="/assets/icons/agents/openclaw.svg"
                  alt="OpenClaw"
                  className="skills-agent-logo skills-agent-logo-primary"
                  title="OpenClaw"
                />
                <img
                  src={isDarkMode ? '/assets/icons/agents/openai-light.svg' : '/assets/icons/agents/openai-dark.svg'}
                  alt="OpenAI"
                  className="skills-agent-logo"
                  title="OpenAI"
                />
                <img
                  src="/assets/icons/agents/claude.png"
                  alt="Claude"
                  className="skills-agent-logo"
                  title="Claude"
                />
                <img
                  src={isDarkMode ? '/assets/icons/agents/cursor-dark.svg' : '/assets/icons/agents/cursor-light.svg'}
                  alt="Cursor"
                  className="skills-agent-logo"
                  title="Cursor"
                />
                <img
                  src={isDarkMode ? '/assets/icons/agents/perplexity-dark.svg' : '/assets/icons/agents/perplexity-light.svg'}
                  alt="Perplexity"
                  className="skills-agent-logo"
                  title="Perplexity"
                />
              </div>
            </div>
            {/* API-key entry point — keys for agent-to-agent access are obtained HERE now (the
                sidebar chip navigates to this page; see sidebar_unified_nav_and_a2a_skills_prd.md). */}
            <div className="skills-hero-cta">
              <SkillsApiKeyCTA />
            </div>
          </div>

          {/* Search */}
          <div className="skills-search-container">
            <Input
              placeholder="Search skills..."
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              allowClear
              className="skills-search-input"
              size="large"
            />
          </div>

          {/* Content */}
          {loading ? (
            <div className="skills-loading">
              <Spin size="large" />
            </div>
          ) : error ? (
            <div className="skills-error">
              <p>{error}</p>
            </div>
          ) : filteredSkills.length === 0 ? (
            <div className="skills-empty">
              <p>No skills match your search.</p>
            </div>
          ) : (
            <div className="skills-catalog">
              {groupedSkills.map(group => (
                <div key={group.id} className="skills-category-section">
                  <div className="skills-category-header">
                    <h2 className="skills-category-title">{group.label}</h2>
                    <p className="skills-category-description">{group.description}</p>
                  </div>
                  <div className="skills-grid">
                    {group.skills.map(skill => (
                      <SkillCard
                        key={skill.slug}
                        skill={skill}
                        onClick={handleSkillClick}
                        isDarkMode={isDarkMode}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SkillsPage;
